import { serializeParams } from "@/utils/apiUtils";
import { AxiosPromise } from "axios";
import { ApiRequestAdapter } from "../../../common/api/ApiRequestAdapter";
import { apiService } from "../../../common/api/apiService";
import { HttpMethod } from "../../../common/api/http";
import { PageSizes } from "../../../common/constants";
import {
  EntityObject,
  OptimisticLockVersionList,
  TwoLevelEntityIdObject,
  TwoLevelEntityObject,
} from "../../../common/types";
import { Commission, UpdateCommission } from "../batch/types";
import { PostponedCommissionsReport } from "./enums";
import {
  DuplicatePostponedCommission,
  PostponedCommissionsExportRequest,
  PostponedCommissionsFilterPageRequest,
  PostponedCommissionsFilterPageResult,
} from "./types";

const FILTER_POSTPONED_COMMISSIONS = ApiRequestAdapter.create("/postponed-commissions", HttpMethod.GET);
const UPDATE_POSTPONED_COMMISSION = ApiRequestAdapter.create("/postponed-commissions/{0}", HttpMethod.PUT);
const RESOLVE_POSTPONED_COMMISSION = ApiRequestAdapter.create("/postponed-commissions/resolve", HttpMethod.POST);
const UNRESOLVE_POSTPONED_COMMISSION = ApiRequestAdapter.create("/postponed-commissions/unresolve", HttpMethod.POST);
const CREATE_DUPLICATED_COMMISSION = ApiRequestAdapter.create(
  "/commissions-batches/{0}/duplicated-commissions",
  HttpMethod.POST,
);
const UPDATE_DUPLICATED_COMMISSION = ApiRequestAdapter.create(
  "/commissions-batches/{0}/duplicated-commissions/{1}",
  HttpMethod.PUT,
);
const DELETE_DUPLICATED_COMMISSION = ApiRequestAdapter.create(
  "/commissions-batches/{0}/duplicated-commissions/{1}",
  HttpMethod.DELETE,
);
const DOWNLOAD_POSTPONED_COMMISSIONS_EXPORT = ApiRequestAdapter.create("/postponed-commissions/export", HttpMethod.GET);

export const commissionPostponedApi = {
  filterPostponedCommissions: (
    request: PostponedCommissionsFilterPageRequest,
  ): AxiosPromise<PostponedCommissionsFilterPageResult> => {
    return apiService.get(FILTER_POSTPONED_COMMISSIONS.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  updatePostponedCommission: (request: EntityObject<UpdateCommission>): AxiosPromise<Commission> => {
    return apiService.put(UPDATE_POSTPONED_COMMISSION.getParametrizedUrl(request.id), request.object);
  },
  resolvePostponedCommission: (request: OptimisticLockVersionList): AxiosPromise<Commission[]> => {
    return apiService.post(RESOLVE_POSTPONED_COMMISSION.url, request);
  },
  unresolvePostponedCommission: (request: OptimisticLockVersionList): AxiosPromise<Commission[]> => {
    return apiService.post(UNRESOLVE_POSTPONED_COMMISSION.url, request);
  },
  createDuplicatedCommission: (request: EntityObject<DuplicatePostponedCommission>): AxiosPromise<Commission> => {
    return apiService.post(CREATE_DUPLICATED_COMMISSION.getParametrizedUrl(request.id), request.object);
  },
  updateDuplicatedCommission: (request: TwoLevelEntityObject<UpdateCommission>): AxiosPromise<Commission> => {
    return apiService.put(UPDATE_DUPLICATED_COMMISSION.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  deleteDuplicatedCommission: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_DUPLICATED_COMMISSION.getParametrizedUrl(request.id1, request.id2));
  },
  downloadPostponedCommissionsExport: (request: PostponedCommissionsExportRequest): AxiosPromise => {
    return apiService.get(DOWNLOAD_POSTPONED_COMMISSIONS_EXPORT.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
      responseType: "blob",
    });
  },
};

export const DEFAULT_POSTPONED_COMMISSIONS_QUERY_PARAMS: Required<PostponedCommissionsFilterPageRequest> = {
  orderBy: [],
  orderDirections: [],
  pageSize: PageSizes.LARGE,
  pageIndex: 0,
  keyword: "",
  report: PostponedCommissionsReport.UNRESOLVED,
  batchId: "",
  postponementReasons: [],
  institutionIds: [],
};
